import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MapPin } from "lucide-react";
import { tenantHref } from "@/lib/tenantPath";
import ContactForm from "@/components/site/ContactForm";
import { TemplateShell } from "./shared/TemplateShell";
import { LuxeHeader, LuxeFooter, LUXE_MAIN_CLASS } from "./chrome/LuxeChrome";
import { useSiteContent, useTestimonials, useBlogPosts, useServices } from "./shared/useSiteData";
import BlogGrid from "./shared/BlogGrid";
// Relative, not "@/": Hero.fillerText.test.js mounts this template with only a handful of
// "@/" modules virtual-mocked (no craco jest moduleNameMapper for "@/") — see Hero.jsx's
// own identical note.
import { hasText } from "../lib/siteContent";
import { formatQuote } from "../lib/testimonials";

/**
 * Luxe / Elegant — dark cinématique + or. Nav glassmorphism logo centré ·
 * hero plein écran image + overlay sombre, serif élégant centré · cartes
 * sombres bordures or, reveal image au survol · story éditoriale lettrines ·
 * citation centrée en fondu · footer minimaliste, fin trait doré.
 */
/**
 * The shared ContactForm in this theme's idiom: nothing but thin gold rules under each
 * field, letterspaced gold micro-labels, and the outlined gold button that fills on hover —
 * the same restraint as the hero and services CTAs. No icon: Luxe's buttons never carry one.
 */
const FORM_CLASSES = {
  grid: "grid sm:grid-cols-2 gap-x-8 gap-y-8 text-left",
  fieldWide: "sm:col-span-2",
  label: "block text-[10px] uppercase tracking-[0.25em] text-[var(--tpl-accent)]",
  input: "mt-3 w-full bg-transparent border-b border-[rgba(212,175,55,0.3)] py-2.5 text-white/90 outline-none focus:border-[var(--tpl-accent)] transition-colors",
  textarea: "mt-3 w-full bg-transparent border-b border-[rgba(212,175,55,0.3)] py-2.5 text-white/90 outline-none focus:border-[var(--tpl-accent)] transition-colors resize-none",
  hint: "mt-4 text-[10px] uppercase tracking-[0.25em] text-white/50",
  actions: "mt-12 flex items-center justify-center gap-8 flex-wrap",
  submit: "inline-flex items-center px-10 py-4 text-sm uppercase tracking-[0.25em] border border-[var(--tpl-accent)] text-[var(--tpl-accent)] hover:bg-[var(--tpl-accent)] hover:text-[var(--tpl-bg)] transition-colors duration-500 disabled:opacity-50",
  success: "text-[10px] uppercase tracking-[0.25em] text-[var(--tpl-accent)]",
  error: "text-[10px] uppercase tracking-[0.25em] text-red-300",
};

export default function LuxeTemplate({ template, toggles = {} }) {
  const c = useSiteContent(toggles);
  const testimonials = useTestimonials();
  const posts = useBlogPosts();
  const services = useServices();
  const [ti, setTi] = React.useState(0);
  const show = (k) => toggles?.[k] !== false;
  const accentFont = { fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.25em" };

  React.useEffect(() => {
    const id = setInterval(() => setTi((a) => (a + 1) % Math.max(testimonials.length, 1)), 6000);
    return () => clearInterval(id);
  }, [testimonials.length]);

  const gold = "var(--tpl-accent)";

  return (
    <TemplateShell template={template} branding={c.branding}>
      <main data-testid="tpl-luxe" className={LUXE_MAIN_CLASS}>
        {/* The split nav is the theme's CHROME (chrome/LuxeChrome.jsx), the same component
            the interior pages render through ThemedPageLayout — never an inline copy. */}
        <LuxeHeader toggles={toggles} />

        {/* HERO — full screen cinématique */}
        <section id="top" className="relative min-h-screen flex items-center justify-center text-center px-6" data-testid="hero-section">
          <img src={c.images.heroBg} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="hero-portrait" />
          <div className="absolute inset-0" style={{ background: "rgba(15,23,42,0.62)" }} />
          <div className="relative max-w-3xl">
            {/* BUG-210: no dictionary sample text backs this any more (lib/content.js) — same
                guard idiom as Hero.jsx's hero-eyebrow. */}
            {hasText(c.hero.eyebrow) && (
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="block text-xs uppercase mb-6" style={{ ...accentFont, color: gold }} data-testid="hero-eyebrow">{c.hero.eyebrow}</motion.span>
            )}
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1 }} className="tpl-head text-5xl md:text-7xl font-medium leading-[1.05]">
              {c.hero.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
            </motion.h1>
            {c.hero.sub && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }} className="mt-7 text-lg md:text-xl text-white/75 max-w-xl mx-auto leading-relaxed">{c.hero.sub}</motion.p>}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-10 flex items-center justify-center gap-5 flex-wrap">
              <a href={tenantHref("/reserver")} data-testid="hero-primary-cta" className="inline-flex items-center gap-2 px-9 py-4 text-sm uppercase border transition-colors duration-500 hover:text-[var(--tpl-bg)]" style={{ ...accentFont, borderColor: gold, color: gold }} onMouseEnter={(e) => (e.currentTarget.style.background = gold)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>{c.hero.cta}</a>
              <a href="#services" className="inline-flex items-center gap-2 text-sm uppercase text-white/70 hover:text-white" style={accentFont}>{c.hero.ctaSecondary} <ArrowRight size={14} /></a>
            </motion.div>
          </div>
          {/* BUG-196: an un-wrapped `flex` row could not drop a stat to a second line,
              so three items wider than the phone dragged the row (and the page, per
              BUG-197) sideways. `flex-wrap` is the fix; `inset-x-0` keeps any wrapped
              row within the viewport rather than centred content overflowing it. */}
          <div data-testid="hero-stats" className="absolute bottom-8 inset-x-0 flex flex-wrap justify-center gap-12">
            {c.hero.stats.map((s, i) => <div key={i} className="text-center"><div className="tpl-head text-2xl" style={{ color: gold }}>{s.v}</div><div className="text-[10px] uppercase text-white/50 mt-1" style={accentFont}>{s.l}</div></div>)}
          </div>
        </section>

        {/* STORY — éditorial lettrine */}
        {show("story") && (
          <section id="story" className="py-28 md:py-36 px-6 md:px-10" data-testid="story-section">
            <div className="max-w-4xl mx-auto">
              <span className="block text-xs uppercase text-center mb-4" style={{ ...accentFont, color: gold }}>{c.story.eyebrow}</span>
              <h2 className="tpl-head text-4xl md:text-6xl font-medium text-center whitespace-pre-line">{c.story.title}</h2>
              <p className="mt-6 text-center text-white/60 text-lg max-w-2xl mx-auto">{c.story.lead}</p>
              <div className="mt-16 space-y-12">
                {c.story.chapters.map((ch, i) => (
                  <motion.div key={ch.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} data-testid={`story-chapter-${ch.n}`} className="border-t pt-10" style={{ borderColor: "rgba(212,175,55,0.2)" }}>
                    <h3 className="tpl-head text-3xl md:text-4xl font-medium"><span className="float-left tpl-head text-6xl mr-3 leading-none" style={{ color: gold }}>{ch.title?.[0]}</span>{ch.title?.slice(1)}</h3>
                    <p className="mt-4 text-white/65 leading-relaxed text-lg">{ch.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* APPROACH — the pillars as gold-ruled editorial columns: a thin rule and a
            letterspaced numeral, no card and no fill, which is the same restraint the story
            chapters and the contact block are drawn with (the bordered cards belong to
            Services, and only to Services). */}
        {show("approach") && (
          <section id="approach" className="py-28 md:py-36 px-6 md:px-10 border-t" style={{ borderColor: "rgba(212,175,55,0.2)" }} data-testid="approach-section">
            <div className="max-w-5xl mx-auto">
              <span className="block text-xs uppercase text-center mb-4" style={{ ...accentFont, color: gold }}>{c.approach.eyebrow}</span>
              <h2 className="tpl-head text-4xl md:text-6xl font-medium text-center whitespace-pre-line">{c.approach.title}</h2>
              <p className="mt-6 text-center text-white/60 text-lg max-w-2xl mx-auto">{c.approach.lead}</p>
              <div className="mt-16 grid sm:grid-cols-2 gap-x-14 gap-y-12">
                {c.approach.pillars.map((p, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.08 }} data-testid={`approach-pillar-${i}`} className="border-t pt-8" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
                    <span className="block text-[10px] uppercase mb-3" style={{ ...accentFont, color: gold }}>{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="tpl-head text-2xl md:text-3xl font-medium">{p.title}</h3>
                    <p className="mt-3 text-white/60 leading-relaxed">{p.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* SERVICES — cartes or reveal */}
        {show("services") && services.length > 0 && (
          <section id="services" className="py-28 md:py-36 px-6 md:px-10" style={{ background: "var(--tpl-secondary)" }} data-testid="services-section">
            <div className="max-w-7xl mx-auto">
              <div className="text-center max-w-2xl mx-auto">
                <span className="block text-xs uppercase mb-4" style={{ ...accentFont, color: gold }}>{c.services.eyebrow}</span>
                <h2 className="tpl-head text-4xl md:text-6xl font-medium whitespace-pre-line">{c.services.title}</h2>
                <p className="mt-6 text-white/60 text-lg">{c.services.lead}</p>
              </div>
              <div className="mt-16 grid md:grid-cols-3 gap-6">
                {services.map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} data-testid={`service-card-${i}`} className="group relative overflow-hidden rounded-sm border p-8 transition-colors duration-500" style={{ borderColor: "rgba(212,175,55,0.3)", background: "var(--tpl-bg)" }}>
                    <div className="text-xs uppercase mb-4" style={{ ...accentFont, color: gold }}>{s.tag} · {s.duration}</div>
                    <h3 className="tpl-head text-2xl md:text-3xl font-medium">{s.title}</h3>
                    <p className="mt-3 text-white/60 leading-relaxed text-sm">{s.body}</p>
                    <a href={tenantHref("/reserver")} className="mt-6 inline-flex items-center gap-2 text-xs uppercase" style={{ ...accentFont, color: gold }}>{c.services.cta} <ArrowRight size={13} /></a>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* TESTIMONIALS — citation centrale fondu */}
        {show("testimonials") && testimonials.length > 0 && (
          <section id="testimonials" className="py-32 px-6" data-testid="testimonials-section">
            <div className="max-w-3xl mx-auto text-center min-h-[200px]">
              <div className="text-5xl tpl-head mb-6" style={{ color: gold }}>“</div>
              <motion.p key={ti} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="tpl-head text-2xl md:text-4xl font-medium italic leading-snug" data-testid={`testimonial-${ti}`}>{formatQuote(testimonials[ti]?.quote)}</motion.p>
              <div className="mt-8 text-xs uppercase" style={{ ...accentFont, color: gold }}>— {testimonials[ti]?.name}</div>
            </div>
          </section>
        )}

        {/* BLOG */}
        {show("blog") && posts.length > 0 && (
          <section id="blog" className="py-28 px-6 md:px-10" style={{ background: "var(--tpl-secondary)" }} data-testid="blog-section">
            <BlogGrid
              posts={posts}
              gridClassName="max-w-7xl mx-auto gap-8"
              cardClassName="group"
              renderCard={(p) => (
                <>
                  <div className="overflow-hidden rounded-sm mb-5"><img src={p.cover} alt="" className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <div className="text-[11px] uppercase" style={{ ...accentFont, color: gold }}>{p.tag} · {p.date}</div>
                  <h3 className="tpl-head text-xl font-medium mt-2">{p.title}</h3>
                  <p className="mt-2 text-white/55 text-sm leading-relaxed">{p.excerpt}</p>
                </>
              )}
            />
          </section>
        )}

        {/* CONTACT */}
        {show("contact") && (
          <section id="contact" className="py-28 md:py-36 px-6 text-center" data-testid="contact-section">
            <div className="max-w-3xl mx-auto">
              <span className="block text-xs uppercase mb-4" style={{ ...accentFont, color: gold }}>{c.contact.eyebrow}</span>
              <h2 className="tpl-head text-4xl md:text-6xl font-medium whitespace-pre-line">{c.contact.title}</h2>
              <p className="mt-6 text-white/60 max-w-xl mx-auto">{c.contact.lead}</p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-8 text-white/70" style={accentFont}>
                <a href={`mailto:${c.contact.info.email}`} className="inline-flex items-center gap-2 text-xs uppercase hover:text-[var(--tpl-accent)]" data-testid="contact-email-link"><Mail size={14} /> {c.contact.info.email}</a>
                <span className="inline-flex items-center gap-2 text-xs uppercase"><MapPin size={14} /> {c.contact.info.place}</span>
              </div>
              <a href={tenantHref("/reserver")} className="mt-10 inline-flex items-center gap-2 px-10 py-4 text-sm uppercase border transition-colors duration-500" style={{ ...accentFont, borderColor: gold, color: gold }} onMouseEnter={(e) => { e.currentTarget.style.background = gold; e.currentTarget.style.color = "var(--tpl-bg)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = gold; }}>{c.nav.book}</a>
              <ContactForm className="mt-16" classNames={FORM_CLASSES} icon={null} successIcon={null} />
            </div>
          </section>
        )}

        <LuxeFooter toggles={toggles} />
      </main>
    </TemplateShell>
  );
}
